import React, { useState, useRef } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { cn } from '../../../utils/cn';

const MessageInput = ({ onSendMessage, disabled = false }) => {
  const [message, setMessage] = useState('');
  const [attachments, setAttachments] = useState([]);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const fileInputRef = useRef(null);
  const textareaRef = useRef(null);

  // Common emojis for quick access
  const quickEmojis = ['😊', '👍', '❤️', '😄', '🙏', '👏', '🔥', '💪', '🎉', '✨'];

  const handleSubmit = (e) => {
    e?.preventDefault();
    
    if (!message?.trim() && attachments?.length === 0) return;
    
    onSendMessage(message?.trim(), attachments);
    setMessage('');
    setAttachments([]);
    
    // Reset textarea height
    if (textareaRef?.current) {
      textareaRef.current.style.height = 'auto';
    }
  };

  const handleKeyPress = (e) => {
    if (e?.key === 'Enter' && !e?.shiftKey) {
      e?.preventDefault();
      handleSubmit(e);
    }
  };

  const handleTextareaChange = (e) => {
    setMessage(e?.target?.value);
    
    // Auto-resize textarea
    const textarea = e?.target;
    textarea.style.height = 'auto';
    textarea.style.height = `${Math.min(textarea?.scrollHeight, 120)}px`;
  };

  const handleFileSelect = (e) => {
    const files = Array.from(e?.target?.files);
    const newAttachments = files?.map(file => ({
      id: Date.now() + Math.random(),
      file,
      name: file?.name,
      size: formatFileSize(file?.size),
      type: file?.type
    }));
    
    setAttachments(prev => [...prev, ...newAttachments]);
    
    // Reset file input
    if (fileInputRef?.current) {
      fileInputRef.current.value = '';
    }
  };

  const removeAttachment = (id) => {
    setAttachments(prev => prev?.filter(attachment => attachment?.id !== id));
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i))?.toFixed(2)) + ' ' + sizes?.[i];
  };

  const addEmoji = (emoji) => {
    setMessage(prev => prev + emoji);
    setShowEmojiPicker(false);
    textareaRef?.current?.focus();
  };

  return (
    <div className="border-t border-border bg-background">
      {/* Attachments Preview */}
      {attachments?.length > 0 && (
        <div className="p-3 border-b border-border">
          <div className="flex flex-wrap gap-2">
            {attachments?.map((attachment) => (
              <div 
                key={attachment?.id}
                className="flex items-center space-x-2 bg-muted p-2 rounded-lg max-w-xs"
              >
                <Icon name="Paperclip" size={14} className="text-muted-foreground flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-foreground truncate">
                    {attachment?.name}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {attachment?.size}
                  </p>
                </div>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => removeAttachment(attachment?.id)}
                  className="h-6 w-6 hover:bg-destructive hover:text-destructive-foreground"
                >
                  <Icon name="X" size={12} />
                </Button>
              </div>
            ))}
          </div>
        </div>
      )}
      {/* Emoji Picker */}
      {showEmojiPicker && (
        <div className="p-3 border-b border-border">
          <div className="flex flex-wrap gap-2">
            {quickEmojis?.map((emoji, index) => (
              <Button
                key={index}
                variant="ghost"
                size="sm"
                onClick={() => addEmoji(emoji)}
                className="h-8 w-8 p-0 hover:bg-muted"
              >
                {emoji}
              </Button>
            ))}
          </div>
        </div>
      )}
      {/* Message Input */}
      <form onSubmit={handleSubmit} className="p-4">
        <div className="flex items-end space-x-3">
          {/* Attachment Button */}
          <div className="flex-shrink-0">
            <input
              ref={fileInputRef}
              type="file"
              multiple
              onChange={handleFileSelect}
              className="hidden"
              accept="*/*"
            />
            <Button
              type="button"
              variant="ghost"
              size="icon"
              onClick={() => fileInputRef?.current?.click()}
              disabled={disabled}
              className="h-10 w-10"
            >
              <Icon name="Paperclip" size={18} />
            </Button>
          </div>

          {/* Text Input */}
          <div className="flex-1 relative">
            <textarea
              ref={textareaRef}
              value={message}
              onChange={handleTextareaChange}
              onKeyPress={handleKeyPress}
              placeholder="Type your message..."
              disabled={disabled}
              className={cn(
                "w-full px-4 py-3 pr-12 bg-muted border border-border rounded-2xl resize-none",
                "text-sm text-foreground placeholder:text-muted-foreground",
                "focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary",
                "transition-all duration-200",
                disabled && "opacity-50 cursor-not-allowed"
              )}
              rows={1}
              style={{ minHeight: '44px', maxHeight: '120px' }}
            />
            
            {/* Emoji Button */}
            <Button
              type="button"
              variant="ghost"
              size="icon"
              onClick={() => setShowEmojiPicker(!showEmojiPicker)}
              disabled={disabled}
              className="absolute right-2 top-1/2 transform -translate-y-1/2 h-8 w-8"
            >
              <Icon name="Smile" size={16} />
            </Button>
          </div>

          {/* Send Button */}
          <Button
            type="submit"
            disabled={disabled || (!message?.trim() && attachments?.length === 0)}
            className="h-10 w-10 rounded-full p-0"
          >
            <Icon name="Send" size={18} />
          </Button>
        </div>

        {/* Input Helper Text */}
        <div className="flex items-center justify-between mt-2 text-xs text-muted-foreground">
          <span>Press Enter to send, Shift+Enter for new line</span>
          <span>{message?.length}/2000</span>
        </div>
      </form>
    </div>
  );
};

export default MessageInput;